/**
 * Where to render the "NEW" divider in a message thread.
 *
 * Starts from `firstUnreadIndex` (the first message the viewer hasn't seen) but
 * skips the viewer's OWN messages: a message you sent is never "new" to you, so
 * the divider belongs above the first message from someone else. Returns -1
 * when there's nothing to mark (no unread, or every unread message is yours).
 */
export function newDividerIndex(
  messages: { authorId: string }[],
  firstUnreadIndex: number,
  currentUserId: string | undefined,
): number {
  if (firstUnreadIndex < 0 || firstUnreadIndex >= messages.length) return -1
  for (let i = firstUnreadIndex; i < messages.length; i++) {
    if (messages[i]?.authorId !== currentUserId) return i
  }
  return -1
}

/**
 * Resolve (and freeze) which message the "NEW" divider sits on.
 *
 * Rules:
 *  - Once a boundary is chosen (`currentBoundaryId`), keep it — later data
 *    churn (e.g. a mark-read refetch flipping unread flags) must not move or
 *    drop the divider before its fade-out finishes.
 *  - Otherwise pick the first-unread message (skipping the viewer's own), BUT
 *    only if it existed when the viewer arrived — i.e. at/before `watermarkId`,
 *    the newest message present on arrival. A message that arrives afterwards
 *    was seen live, so it never spawns a divider (prevents the flash while
 *    live-chatting). Returns null until a valid boundary exists — the boundary
 *    input can resolve async (chat's last-seen id loads after the messages).
 */
export function resolveDividerBoundaryId(
  messages: { id: string; authorId: string }[],
  firstUnreadIndex: number,
  currentUserId: string | undefined,
  watermarkId: string | undefined,
  currentBoundaryId: string | null,
): string | null {
  if (currentBoundaryId) return currentBoundaryId
  if (!watermarkId) return null
  const idx = newDividerIndex(messages, firstUnreadIndex, currentUserId)
  const watermarkIdx = messages.findIndex((m) => m.id === watermarkId)
  if (idx >= 0 && watermarkIdx >= 0 && idx <= watermarkIdx) {
    return messages[idx]?.id ?? null
  }
  return null
}
